import { getReportFooterText } from '../reportFooter';
export interface AssetAcquisitionOrderHeaderInfo {
  prDescription?: string | null;
  priority?: string | null;
  assetAuthority?: string | null;
  orderNumber?: string | null;
  orderDate?: string | null;
  expectedDeliveryDate?: string | null;
  purchaseType?: string | null;
  orderStatus?: string | null;
  vendor?: string | null;
  apc?: string | null;
  requiredByDate?: string | null;
  totalCost?: number | null;
}

export interface AssetAcquisitionOrderItem {
  lin?: string | null;
  nsn?: string | null;
  nomenclature: string;
  quantityOrdered?: number | null;
  unitCost?: number | null;
  totalLineCost?: number | null;
}

export interface AssetAcquisitionOrderTemplateOptions {
  generatedAt: Date;
  header: AssetAcquisitionOrderHeaderInfo;
  items: AssetAcquisitionOrderItem[];
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const sanitizeText = (value: string | null | undefined): string => {
  const trimmed = value?.toString().trim();
  if (!trimmed) {
    return '—';
  }
  return escapeHtml(trimmed);
};

const formatCurrency = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '—';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(value);
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return sanitizeText(value);
  }
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

const formatDateTime = (value: Date): string =>
  new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(value);

const buildItemsTable = (items: AssetAcquisitionOrderItem[]): string => {
  if (items.length === 0) {
    return `
      <tr>
        <td colspan="6" class="empty-row">
          No line items found for this order.
        </td>
      </tr>
    `;
  }

  return items
    .map(
      (item) => `
        <tr>
          <td>${sanitizeText(item.lin)}</td>
          <td>${sanitizeText(item.nsn)}</td>
          <td>${sanitizeText(item.nomenclature)}</td>
          <td class="numeric">${sanitizeText(
            item.quantityOrdered !== null && item.quantityOrdered !== undefined
              ? String(item.quantityOrdered)
              : '',
          )}</td>
          <td class="numeric">${formatCurrency(item.unitCost ?? null)}</td>
          <td class="numeric">${formatCurrency(item.totalLineCost ?? null)}</td>
        </tr>
      `,
    )
    .join('');
};

export const buildAssetAcquisitionOrderHtml = ({
  generatedAt,
  header,
  items,
}: AssetAcquisitionOrderTemplateOptions): string => {
  const generatedLabel = formatDateTime(generatedAt);
  const orderDateLabel = formatDate(header.orderDate ?? null);
  const expectedDeliveryLabel = formatDate(header.expectedDeliveryDate ?? null);
  const requiredByLabel = formatDate(header.requiredByDate ?? null);
  const totalCostLabel = formatCurrency(header.totalCost ?? null);
  const itemsTable = buildItemsTable(items);

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>Asset Acquisition Order Form</title>
    <style>
      :root {
        color-scheme: light;
      }

      * {
        box-sizing: border-box;
      }

      body {
        margin: 16px;
        font-family: 'Roboto', 'Helvetica Neue', Arial, sans-serif;
        color: #15263c;
        background: #ffffff;
      }

      header {
        margin-bottom: 16px;
      }

      .title {
        font-size: 24px;
        font-weight: 700;
        margin: 0;
        text-align: center;
      }

      .meta-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 8px 16px;
        margin-top: 12px;
        margin-bottom: 8px;
      }

      .meta-block {
        font-size: 14px;
      }

      .meta-label {
        font-weight: 600;
        margin-right: 4px;
      }

      .meta-row-inline {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 12px;
        border: 1px solid #d7e3f4;
      }

      thead th {
        background: #e9f1ff;
        text-align: left;
        font-size: 12px;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        padding: 10px 12px;
        color: #4a5c72;
        border-bottom: 1px solid #d7e3f4;
      }

      tbody td {
        padding: 10px 12px;
        border-bottom: 1px solid #edf3fc;
        font-size: 14px;
        color: #1f2f45;
      }

      tbody tr:last-child td {
        border-bottom: none;
      }

      tbody tr:nth-child(even) td {
        background: #f9fbff;
      }

      .numeric {
        text-align: center;
        font-variant-numeric: tabular-nums;
      }

      .empty-row {
        text-align: center;
        font-style: italic;
        color: #6b7c92;
        padding: 24px 12px;
      }

      footer {
        margin-top: 24px;
        font-size: 12px;
        color: #5c6f85;
        text-align: center;
      }

      @media print {
        body {
          margin: 8mm;
        }
      }
    </style>
  </head>
  <body>
    <header>
      <h1 class="title">Asset Acquisition Order Form</h1>
      <div class="meta-grid">
        <div class="meta-block">
          <span class="meta-label">PR Description:</span>
          <span>${sanitizeText(header.prDescription)}</span>
        </div>
        <div class="meta-block">
          <span class="meta-label">Asset Authority:</span>
          <span>${sanitizeText(header.assetAuthority)}</span>
        </div>
        <div class="meta-block">
          <span class="meta-label">Order Number:</span>
          <span>${sanitizeText(header.orderNumber)}</span>
        </div>

        <div class="meta-block">
          <span class="meta-label">Priority:</span>
          <span>${sanitizeText(header.priority)}</span>
        </div>
        <div class="meta-block">
          <span class="meta-label">Purchase Type:</span>
          <span>${sanitizeText(header.purchaseType)}</span>
        </div>
        <div class="meta-block">
          <span class="meta-label">Vendor:</span>
          <span>${sanitizeText(header.vendor)}</span>
        </div>

        <div class="meta-block">
          <span class="meta-label">Order Date:</span>
          <span>${orderDateLabel}</span>
        </div>
        <div class="meta-block">
          <span class="meta-label">Order Status:</span>
          <span>${sanitizeText(header.orderStatus)}</span>
        </div>
        <div class="meta-block">
          <span class="meta-label">APC:</span>
          <span>${sanitizeText(header.apc)}</span>
        </div>

        <div class="meta-block">
          <span class="meta-label">Expected Delivery Date:</span>
          <span>${expectedDeliveryLabel}</span>
        </div>
        <div class="meta-block">
          <span class="meta-label">Required By Date:</span>
          <span>${requiredByLabel}</span>
        </div>
        <div class="meta-block meta-row-inline">
          <span class="meta-label">Total Cost:</span>
          <span>${totalCostLabel}</span>
        </div>
      </div>
    </header>

    <section>
      <table>
        <thead>
          <tr>
            <th>LIN</th>
            <th>NSN</th>
            <th>Nomenclature</th>
            <th class="numeric">Qty Ordered</th>
            <th class="numeric">Unit Cost</th>
            <th class="numeric">Total Line Cost</th>
          </tr>
        </thead>
        <tbody>
          ${itemsTable}
        </tbody>
      </table>
    </section>

    <footer>
      Generated ${escapeHtml(generatedLabel)} · ${getReportFooterText()}
    </footer>
  </body>
</html>`;
};
